import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion';
import Icon from './Icon';
import Magnetic from '../motion/Magnetic';
import { NAV_LINKS, CONTACT } from '../data/site';

const ease = [0.76, 0, 0.24, 1];
const soft = [0.22, 1, 0.36, 1];

export default function Navbar() {
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const { pathname } = useLocation();
  const { scrollY } = useScroll();

  // Hide while scrolling down, show when scrolling up
  useMotionValueEvent(scrollY, 'change', (y) => {
    const prev = scrollY.getPrevious() ?? 0;
    setScrolled(y > 20);
    setHidden(y > 300 && y > prev && !open);
  });

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const isActive = (p) => (p === '/' ? pathname === '/' : pathname.startsWith(p));

  return (
    <>
      <motion.header className={`nav${scrolled ? ' scrolled' : ''}`}
        initial={{ y: -120, opacity: 0 }} animate={{ y: hidden ? -130 : 0, opacity: 1 }} transition={{ duration: 0.7, ease: soft }}>
        <div className="nav-shell">
          <span className="nav-border" aria-hidden="true" />

          <Link to="/" className="nav-logo" aria-label="Blue Block PMC home">
            <motion.span className="mark" initial={{ rotate: -90, scale: 0.4, opacity: 0 }} animate={{ rotate: 0, scale: 1, opacity: 1 }} transition={{ delay: 0.2, duration: 0.9, ease: soft }}>
              <img src="/logo-160.png" alt="" width="44" height="44" />
            </motion.span>
            <motion.span className="nav-logo-text" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35, duration: 0.7, ease: soft }}>
              <strong>Blue Block</strong><span>PMC · Coimbatore</span>
            </motion.span>
          </Link>

          <nav aria-label="Main">
            <ul className="nav-links" onMouseLeave={() => setHover(null)}>
              {NAV_LINKS.map((l, i) => {
                const active = isActive(l.path);
                return (
                  <motion.li key={l.path} style={{ position: 'relative' }} onMouseEnter={() => setHover(l.path)}
                    initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.06, duration: 0.6, ease: soft }}>
                    <NavLink to={l.path} end={l.path === '/'} className={`nav-link${active ? ' active' : ''}`}>
                      {hover === l.path && !active && <motion.span layoutId="nav-hover" className="nav-hover" transition={{ type: 'spring', stiffness: 420, damping: 34 }} />}
                      {active && <motion.span layoutId="nav-pill" className="nav-pill" transition={{ type: 'spring', stiffness: 380, damping: 30 }} />}
                      <span className="nav-label">{l.label}</span>
                    </NavLink>
                  </motion.li>
                );
              })}
            </ul>
          </nav>

          <motion.div className="nav-actions" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5, duration: 0.7, ease: soft }}>
            <a href={CONTACT.phoneHref} className="nav-call" aria-label={`Call ${CONTACT.phone}`}><Icon name="phone" size={17} /></a>
            <Magnetic strength={0.25}>
              <Link to="/request-consultation" className="nav-cta">
                <span>Let's Talk</span>
                <span className="nav-cta-arrow"><Icon name="arrow" size={14} /></span>
              </Link>
            </Magnetic>
            <button className={`nav-toggle${open ? ' open' : ''}`} onClick={() => setOpen((o) => !o)} aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open}>
              <span className="burger"><i /><i /><i /></span>
            </button>
          </motion.div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div className="menu"
            initial={{ clipPath: 'circle(0% at calc(100% - 52px) 50px)' }}
            animate={{ clipPath: 'circle(150% at calc(100% - 52px) 50px)', transition: { duration: 0.85, ease } }}
            exit={{ clipPath: 'circle(0% at calc(100% - 52px) 50px)', transition: { duration: 0.55, ease } }}>
            <div className="menu-glow" />
            <nav className="menu-links">
              {[...NAV_LINKS, { path: '/request-consultation', label: 'Contact' }].map((l, i) => (
                <motion.div key={l.path} initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1, transition: { delay: 0.2 + i * 0.07, duration: 0.7, ease: soft } }} exit={{ opacity: 0, transition: { duration: 0.15 } }}>
                  <Link to={l.path} className={`menu-link${isActive(l.path) ? ' active' : ''}`}>
                    <small>0{i + 1}</small><span>{l.label}</span><Icon name="arrow" size={22} />
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.div className="menu-foot" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.55 } }}>
              <a href={CONTACT.phoneHref}><Icon name="phone" size={16} />{CONTACT.phone}</a>
              <a href={`mailto:${CONTACT.email}`}><Icon name="mail" size={16} />{CONTACT.email}</a>
              <Link to="/request-consultation" className="btn btn-primary btn-block" style={{ marginTop: 8 }}>Request Consultation <span className="arrow"><Icon name="arrow" size={14} /></span></Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
